// What's-New popup — the PAGER DECISION, as a PURE function (0.12.5 Part K).
//
// Ben, 2026-09-07. With several releases going out in quick succession, someone returning after a
// few days meets a STACK of unread notes. The popup already handles the stack correctly and shows
// all of them. What it got wrong was how easy it was to dismiss the whole stack having read one:
//
//   • fetchWhatsNew(seen) returns every note newer than the user's seen version and setItems(mine)
//     keeps ALL of them, one per page.
//   • The pager was TWO 11px CHEVRONS and a "1 of 4" in the header — browsing, not a flow.
//   • "Got it" sat on EVERY page, and dismiss() writes __APP_VERSION__ — THE RUNNING BUILD, not
//     the version of the note being read. So page 1 of 4 dismissed all four.
//   • The footer line "Won't show again on any of your devices" also showed on every page,
//     including ones where the button would not dismiss anything.
//
// The rule: with MORE THAN ONE note, and NOT on the last page, the primary button reads "Next" and
// advances. On the last page — and whenever there is only one note — it reads "Got it" and
// dismisses exactly as before.
//
// ─── WHY THIS IS A SEPARATE FILE ─────────────────────────────────────────────────────────────
// There is no frontend test runner in this project, so a React component cannot be imported by a
// verify script. The decision is therefore lifted out whole, exactly as the booking form's submit
// chain was (lib/ops-board/bookingSubmit.ts), and database/verify-whatsnew-pager.ts imports THIS.
// No React, no vite-only imports, no __APP_VERSION__ — keep it that way or the gate stops running.
//
// ⚠ THE THREE OUTPUTS MOVE TOGETHER, WHICH IS THE POINT. `primaryDismisses` and `showDismissNote`
// are both derived from the same `isLast` and are deliberately NOT independent inputs to the
// component: the footer promises what the button does, so a page where the button will not dismiss
// must not carry the promise. Wiring either of them separately in the component would let the two
// drift apart again, which is the defect in a different shape.

export interface WhatsNewPagerState {
  /** The chevron row + "N of M" in the header. Only ever shown for a stack. */
  showPager: boolean;
  /** The primary button's text. */
  primaryLabel: 'Next' | 'Got it';
  /**
   * Whether the primary button DISMISSES (writes the seen version) or merely advances the page.
   * ⚠ False on a "Next" page is the whole point of Part K: pressing Next must not write the seen
   * version, because the reader has not been offered the rest of the stack yet.
   */
  primaryDismisses: boolean;
  /**
   * Whether to show "Won't show again on any of your devices". Tied to `primaryDismisses`: on a
   * "Next" page the line describes something the button will not do.
   */
  showDismissNote: boolean;
}

/**
 * Decide the popup's footer state for a stack of `itemCount` notes showing page `page`.
 *
 * `page` is clamped the same way the component clamps its render index
 * (`items[Math.min(page, items.length - 1)]`), so an out-of-range page cannot produce a "Next" that
 * leads nowhere.
 *
 * @param itemCount how many unread notes this user has (>= 0)
 * @param page      the zero-based page being shown
 */
export function whatsNewPagerState(itemCount: number, page: number): WhatsNewPagerState {
  // A stack of one (or none — the component returns null before this is reached) is the COMMON
  // PATH and must behave exactly as it did before 0.12.5: "Got it", the note, no pager.
  const isStack = itemCount > 1;
  const clamped = Math.max(0, Math.min(page, Math.max(0, itemCount - 1)));
  const isLast = clamped >= itemCount - 1;
  const dismisses = !isStack || isLast;
  return {
    showPager: isStack,
    primaryLabel: dismisses ? 'Got it' : 'Next',
    primaryDismisses: dismisses,
    showDismissNote: dismisses,
  };
}
